import React from 'react';
import { Box, Typography, MenuItem, InputAdornment, Alert, Button, IconButton, Stack } from '@mui/material';
import { 
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Payment as PaymentIcon,
  AccountBalance as DepositIcon,
  Info as InfoIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { useFormContext, useFieldArray } from 'react-hook-form';
import FormTextField from '../../shared/FormTextField';
import FormSelect from '../../shared/FormSelect';
import FormCheckboxGroup from '../../shared/FormCheckboxGroup';
import StepFormLayout from '../../shared/StepFormLayout';
import FormSection from '../../shared/FormSection';

const paymentMethodOptions = [
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Personal Check' },
  { value: 'cashiers_check', label: "Cashier's Check" },
  { value: 'money_order', label: 'Money Order' },
  { value: 'ach', label: 'ACH/Direct Deposit' },
  { value: 'online', label: 'Online Portal' },
  { value: 'zelle', label: 'Zelle' },
  { value: 'venmo', label: 'Venmo' },
];

interface FinancialTermsStepProps {
  stepProgress: number;
}

const FinancialTermsStep: React.FC<FinancialTermsStepProps> = ({ stepProgress }) => {
  const { control, watch } = useFormContext();
  const lateFeeType = watch('rent.lateFee.type');
  const otherDeposits = useFieldArray({
    control,
    name: 'deposits.otherDeposits',
  });
  const progress = stepProgress;

  return (
    <StepFormLayout
      title="Financial Terms"
      description="Set rent amounts, late fees, and deposit information"
      icon={<MoneyIcon sx={{ fontSize: 28, color: 'primary.main' }} />}
      progress={progress}
    >
      <Box>
      
      {/* Quick tip */}
      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }} icon={<InfoIcon />}>
        <Typography variant="body2">
          <strong>💰 Tip:</strong> Ensure all financial terms comply with local rent control and security deposit laws.
        </Typography>
      </Alert>
      
      {/* Monthly Rent */}
      <FormSection
        title="Monthly Rent"
        icon={<MoneyIcon color="primary" />}
        tooltip="Base monthly rent amount and due date"
        isRequired={true}
        defaultExpanded={true}
      >
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap' }}>
          <FormTextField
            name="rent.monthlyAmount"
            control={control}
            label="Monthly Rent Amount"
            type="number"
            required
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          />
          
          <FormSelect
            name="rent.dueDay"
            control={control}
            label="Rent Due Day"
            required
          >
            {[...Array(31)].map((_, i) => (
              <MenuItem key={i + 1} value={i + 1}>
                {i + 1} of each month
              </MenuItem>
            ))}
          </FormSelect>

          <FormTextField
            name="rent.dueTime"
            control={control}
            label="Rent Due Time (optional)"
            placeholder="e.g., by 5pm on the 1st"
            InputProps={{
              startAdornment: <InputAdornment position="start"><AccessTimeIcon fontSize="small" /></InputAdornment>,
            }}
          />

          <FormTextField
            name="rent.prorated"
            control={control}
            label="Prorated First Month Rent"
            type="number"
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            helperText="Amount due for a partial first month (if applicable)"
          />
        </Box>
      </FormSection>

      {/* Deposits & Fees */}
      <FormSection
        title="Deposits & Fees"
        icon={<DepositIcon color="primary" />}
        tooltip="Security deposits and additional fees"
        isRequired={true}
        defaultExpanded={false}
      >
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap' }}>
          <FormTextField
            name="deposits.security"
            control={control}
            label="Security Deposit"
            type="number"
            required
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          />
          
          
          
          <FormTextField
            name="fees.nsfFee"
            control={control}
            label="NSF (Non-Sufficient Funds) Fee"
            type="number"
            required
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            helperText="Charge applied when a tenant payment is returned or bounces"
          />
        </Box>

        <Stack spacing={2} sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Other Deposits (optional)</Typography>
          {otherDeposits.fields.map((field, index) => (
            <Box
              key={field.id}
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                alignItems: { xs: 'flex-start', sm: 'center' },
              }}
            >
              <FormTextField
                name={`deposits.otherDeposits.${index}.description`}
                control={control}
                label="Deposit Label"
                placeholder="e.g., Cleaning, Fob, Remote"
              />
              <FormTextField
                name={`deposits.otherDeposits.${index}.amount`}
                control={control}
                label="Amount"
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
              <IconButton
                aria-label="Remove deposit"
                onClick={() => otherDeposits.remove(index)}
                sx={{ mt: { xs: 0, sm: 0.5 } }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => otherDeposits.append({ description: '', amount: 0 })}
            size="small"
            sx={{ alignSelf: 'flex-start' }}
          >
            Add Other Deposit
          </Button>
        </Stack>
      </FormSection>

      <FormSection
        title="Nonrefundable Fees"
        icon={<DepositIcon color="secondary" />}
        tooltip="Capture any nonrefundable move-in or administrative fees"
        isRequired={false}
        defaultExpanded={false}
      >
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <FormTextField
            name="fees.nonRefundableTotal"
            control={control}
            label="Total Nonrefundable Fees"
            type="number"
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            helperText="Sum of all nonrefundable move-in or admin fees"
          />
          <FormTextField
            name="fees.nonRefundableDescription"
            control={control}
            label="Fee Description"
            placeholder="e.g., Move-in processing fee"
          />
        </Box>
      </FormSection>
      
      {/* Late Fees */}
      <FormSection
        title="Late Fees"
        icon={<ScheduleIcon color="primary" />}
        tooltip="Late fee structure and grace period"
        isRequired={false}
        defaultExpanded={false}
      >
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <FormSelect
            name="rent.lateFee.type"
            control={control}
            label="Late Fee Type"
            required
          >
            <MenuItem value="fixed">Fixed Amount</MenuItem>
            <MenuItem value="percentage">Percentage of Rent</MenuItem>
          </FormSelect>
          
          <FormTextField
            name="rent.lateFee.amount"
            control={control}
            label={lateFeeType === 'fixed' ? 'Late Fee Amount' : 'Late Fee Percentage'}
            type="number"
            required
            InputProps={{
              startAdornment: <InputAdornment position="start">
                {lateFeeType === 'fixed' ? '$' : ''}
              </InputAdornment>,
              endAdornment: lateFeeType === 'percentage' ? 
                <InputAdornment position="end">%</InputAdornment> : null,
            }}
          />
          
          <FormTextField
            name="rent.lateFee.gracePeriod"
            control={control}
            label="Grace Period (Days)"
            type="number"
            required
          />
        </Box>
      </FormSection>
      
      {/* Accepted Payment Methods */}
      <FormSection
        title="Accepted Payment Methods"
        icon={<PaymentIcon color="primary" />}
        tooltip="Select all payment methods you will accept"
        isRequired={false}
        defaultExpanded={false}
      >
        <FormCheckboxGroup
          name="rent.paymentMethods"
          control={control}
          options={paymentMethodOptions}
        />
      </FormSection>
      </Box>
    </StepFormLayout>
  );
};

export default FinancialTermsStep; 
